import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CustomGridStackWidget } from '../../../services/grid-widget-config.service';
import { getState } from '@ngrx/signals';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { MenuModule } from 'primeng/menu';
import { WegweiserStore } from '../../../stores/wegweiser-api/wegweiser.state';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { DataGroupProperty } from '../../../stores/indicatpr-data/indicator-data.widget-selectors';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-wegweiser-fast-switch',
  standalone: true,
  imports: [
    TieredMenuModule,
    MenuModule,
    DropdownModule,
    FormsModule,
    FormFieldWrapperComponent,
    NgClass,
  ],
  templateUrl: './wegweiser-fast-switch.component.html',
  styleUrl: './wegweiser-fast-switch.component.scss',
})
export class WegweiserFastSwitchComponent implements OnInit {
  @Input() widget!: CustomGridStackWidget;
  @Input() variant: 'menu' | 'dropdown' = 'menu';
  @Input() readonly = false;
  @Output() selectionChange = new EventEmitter<number>();

  readonly widgetStore = inject(WidgetStore);
  readonly wegweiserStore = inject(WegweiserStore);

  public labels: Record<DataGroupProperty, string> = {
    indicator: 'Der angezeigte Indikator ist ...',
    dataLayer: 'Die angezeigte Jahr ist ...',
    feature: 'Das angezeigte Region ist ...',
  };

  activeCaption = '';
  selection: number | null = null;
  menuItems: MenuItem[] = [];

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of the directive.
   * This method is used to perform component initialization logic, such as setting up state or invoking setup utilities.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.initSwitcher();
  }

  constructor() {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      const wegweiserState = getState(this.wegweiserStore);
      if (wegweiserState.topics.length === 0) return;
      if (
        !this.activeCaption ||
        this.widget._id === widgetState.rerenderId ||
        widgetState.rerenderIds.includes(this.widget._id)
      ) {
        this.initSwitcher();
      }
    });
  }

  /**
   * Initializes the switcher based on the widget's focus property and associated data.
   * This method dynamically sets properties such as `selection` and `activeCaption`,
   * and generates menu items based on the focus property type (indicator, feature, or dataLayer).
   *
   * @return {void} Does not return a value.
   */
  initSwitcher(): void {
    const { focusProperty, wegweiserData } = this.widget.widget;
    if (!wegweiserData) return;

    const focusHandlers: Record<DataGroupProperty, () => void> = {
      indicator: () => {
        this.selection = Number(this.widget.widget.indicator);
        this.activeCaption = this.wegweiserStore.getIndicatorCaption(
          this.widget.widget.indicator
        );
        this.generateMenuItems(wegweiserData.indicators, 'name', 'id');
      },
      feature: () => {
        this.selection = Number(this.widget.widget.feature);
        this.activeCaption = this.wegweiserStore.getRegionCaption(
          Number(this.widget.widget.feature)
        );
        this.generateMenuItems(wegweiserData.regions, 'name', 'id');
      },
      dataLayer: () => {
        this.selection = Number(this.widget.widget.year);
        this.activeCaption = this.wegweiserStore.getYearCaption(
          this.widget.widget.year
        );
        const rYears = [...wegweiserData.years].reverse();
        this.generateMenuItems(rYears, undefined, undefined, true);
      },
    };

    // Dynamically call the handler based on focusProperty
    focusHandlers[focusProperty]?.();
  }

  /**
   * Generates menu items for a dropdown or menu component based on the input items.
   * Each generated menu item includes a label, a value, and a command action.
   *
   * @param {T[]} items - The array of items used to generate the menu items.
   * @param {keyof T} [labelKey] - The property key in the items used to set the label for each menu item.
   * @param {keyof T} [valueKey] - The property key in the items used to set the value for each menu item.
   * @param {boolean} [isDirectValue=false] - Indicates whether the item itself should be treated as the direct label and value, bypassing the keys.
   * @return {void} This method does not return a value as it generates and assigns menu items to the class property.
   */
  private generateMenuItems<T>(
    items: T[],
    labelKey?: keyof T,
    valueKey?: keyof T,
    isDirectValue = false
  ): void {
    this.menuItems = items.map(item => ({
      label: isDirectValue ? String(item) : String(item[labelKey as keyof T]),
      value: isDirectValue ? item : item[valueKey as keyof T],
      command: (event: MenuItemCommandEvent) => {
        this.updateChart(
          isDirectValue
            ? (item as unknown as number) // handle direct values (e.g., years)
            : (item[valueKey as keyof T] as string | number) // narrow the type explicitly
        );
      },
    }));
  }

  /**
   * Updates the chart based on the provided value and the current focus property.
   *
   * @param {number | string} value - The value to update the chart with. Can be a number or a string.
   * @return {void} No return value.
   */
  protected updateChart(value: number | string): void {
    const { focusProperty } = this.widget.widget;

    const updateMap: Record<string, () => void> = {
      indicator: () => (this.widget.widget.indicator = String(value)),
      feature: () => (this.widget.widget.feature = String(value)),
      dataLayer: () => (this.widget.widget.year = String(value)),
    };

    // Update the required focus property based on focusProperty
    updateMap[focusProperty]?.();

    this.widgetStore.update(this.widget._id, this.widget, true);
    this.selectionChange.emit(Number(value));
  }
}
